import React, { useState, useRef, useEffect, useCallback } from 'react';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from '../utils/googleMapsLoader';

interface LocationDetails {
  suburb?: string;
  postcode?: string;
  council?: string;
  latitude?: number;
  longitude?: number;
  displayText: string;
}

interface LocationSearchInputProps {
  onLocationSelect: (location: LocationDetails) => void;
  placeholder?: string;
  initialValue?: string;
  className?: string;
  disabled?: boolean;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  onLocationSelect,
  placeholder = "Search by suburb or postcode",
  initialValue = "",
  className = "",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Initialize Google Places services
  useEffect(() => {
    const initializeServices = async () => {
      try {
        if (!isGoogleMapsLoaded()) {
          await loadGoogleMapsAPI();
        }

        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          
          const dummyDiv = document.createElement('div');
          placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
      } catch (error) {
        console.error('Failed to initialize Google Maps services:', error);
      }
    };

    initializeServices();
  }, []);

  // Handle input changes with debouncing
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setSelectedIndex(-1);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Check if input looks like a postcode (4 digits)
    const postcodePattern = /^\d{4}$/;
    if (postcodePattern.test(value.trim())) {
      // Direct postcode match
      const locationDetails: LocationDetails = {
        postcode: value.trim(),
        displayText: value.trim()
      };
      onLocationSelect(locationDetails);
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    if (value.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  }, [onLocationSelect]);

  // Fetch location predictions from Google Places API
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteService.current) return;

    setIsLoading(true);

    const request = {
      input,
      componentRestrictions: { country: 'au' },
      types: ['(regions)'], // Focus on suburbs, localities, and administrative areas
    };

    autocompleteService.current.getPlacePredictions(request, (predictions: any, status: any) => {
      setIsLoading(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        // Filter to prioritize suburbs and localities
        const filteredPredictions = predictions.filter((prediction: any) => {
          const types = prediction.types || [];
          return types.includes('locality') || 
                 types.includes('sublocality') || 
                 types.includes('administrative_area_level_2') ||
                 types.includes('postal_code');
        });
        
        setPredictions(filteredPredictions);
        setShowDropdown(filteredPredictions.length > 0);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    });
  }, []);

  // Get detailed place information
  const getPlaceDetails = useCallback((placeId: string) => {
    if (!placesService.current) return;

    const request = {
      placeId,
      fields: [
        'formatted_address',
        'address_components',
        'geometry.location',
        'types'
      ]
    };

    placesService.current.getDetails(request, (place: any, status: any) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const locationDetails = parseLocationComponents(place);
        onLocationSelect(locationDetails);
        setShowDropdown(false);
      }
    });
  }, [onLocationSelect]);

  // Parse Google Places components for location search
  const parseLocationComponents = (place: any): LocationDetails => {
    const components = place.address_components || [];
    
    let suburb = '';
    let council = '';
    let postcode = '';

    components.forEach((component: any) => {
      const types = component.types;
      
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        suburb = component.long_name;
      } else if (types.includes('administrative_area_level_2')) {
        council = component.long_name;
      } else if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
    });

    // Get coordinates if available
    const location = place.geometry?.location;
    const latitude = location?.lat() || undefined;
    const longitude = location?.lng() || undefined;

    // Create display text based on available information
    let displayText = '';
    if (suburb && postcode) {
      displayText = `${suburb} ${postcode}`;
    } else if (suburb) {
      displayText = suburb;
    } else if (postcode) {
      displayText = postcode;
    } else {
      displayText = place.formatted_address || '';
    }

    return {
      suburb: suburb || undefined,
      council: council || undefined,
      postcode: postcode || undefined,
      latitude,
      longitude,
      displayText
    };
  };

  // Handle prediction selection
  const handlePredictionSelect = useCallback((prediction: PlacePrediction) => {
    setInputValue(prediction.description);
    getPlaceDetails(prediction.place_id);
  }, [getPlaceDetails]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handlePredictionSelect(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, predictions, selectedIndex, handlePredictionSelect]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-urban-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cardboard bg-sky-neutral ${className}`}
          autoComplete="off"
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cardboard"></div>
          </div>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-teddy border border-urban-300 rounded-md shadow-card max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className={`px-4 py-3 cursor-pointer hover:bg-teddy-500 border-b border-urban-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-cardboard-50 border-cardboard-200' : ''
              }`}
            >
              <div className="flex flex-col">
                <span className="font-medium text-urban">
                  {prediction.structured_formatting.main_text}
                </span>
                <span className="text-sm text-urban-300">
                  {prediction.structured_formatting.secondary_text}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && predictions.length === 0 && !isLoading && inputValue.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-teddy border border-urban-300 rounded-md shadow-card"
        >
          <div className="px-4 py-3 text-urban-300 text-sm">
            No locations found. Try a suburb name or 4-digit postcode.
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSearchInput;